import React, { useState } from 'react';
import { Card, Col, Row, Button, Alert, Collapse } from 'react-bootstrap';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import {
  faInfoCircle,
  faExternalLinkAlt
} from '@fortawesome/free-solid-svg-icons';

interface MicrosoftFormPageProps {
  url: string;
  title: string;
}

const isSafari = (() => {
  if (typeof navigator === 'undefined') return false;
  const ua = navigator.userAgent;
  return /safari/i.test(ua) && !/chrome|crios|fxios|edg|edgios|opr/i.test(ua);
})();

const MicrosoftFormPage: React.FC<MicrosoftFormPageProps> = ({
  url,
  title
}) => {
  const [showInstructions, setShowInstructions] = useState(false);

  const handleOpenForm = () => {
    window.open(url, '_blank', 'noopener,noreferrer');
  };

  if (isSafari) {
    return (
      <Row className="gy-3 mb-6 align-items-center">
        <Col xs={12}>
          <h2 className="mb-4">{title}</h2>
          <Card className="text-center">
            <Card.Body className="py-6 px-4">
              <FontAwesomeIcon
                icon={faExternalLinkAlt}
                size="3x"
                className="text-primary mb-3"
              />
              <h4 className="mb-2">Open this form in a new window</h4>
              <p className="text-muted mb-4">
                Safari blocks embedded Microsoft Forms for privacy reasons. Use
                the button below to fill out the form in a new tab.
              </p>
              <Button
                variant="primary"
                size="lg"
                onClick={handleOpenForm}
                className="d-inline-flex align-items-center gap-2"
              >
                <span>Open Form</span>
                <FontAwesomeIcon icon={faExternalLinkAlt} />
              </Button>
              <div className="mt-4">
                <Button
                  variant="link"
                  size="sm"
                  className="p-0"
                  onClick={() => setShowInstructions(!showInstructions)}
                >
                  Or enable embedding in Safari
                </Button>
                <Collapse in={showInstructions}>
                  <div className="mt-2 text-start d-inline-block">
                    <ol className="mb-0 ps-3">
                      <li>Open Safari → Settings → Privacy</li>
                      <li>Uncheck "Prevent cross-site tracking"</li>
                      <li>Refresh this page</li>
                    </ol>
                  </div>
                </Collapse>
              </div>
            </Card.Body>
          </Card>
        </Col>
      </Row>
    );
  }

  return (
    <Row className="gy-3 mb-6 align-items-center">
      <Col xs={12}>
        <h2 className="mb-4">{title}</h2>

        <Alert variant="warning" className="mb-3">
          <div className="d-flex align-items-start gap-2">
            <FontAwesomeIcon
              icon={faInfoCircle}
              className="flex-shrink-0 mt-1"
              size="lg"
            />
            <div className="flex-grow-1">
              <strong>Can't see the form?</strong>
              <p className="mb-2">
                Firefox may block embedded forms. Click below to open in a new
                window.
              </p>
              <Button
                variant="link"
                size="sm"
                className="p-0"
                onClick={() => setShowInstructions(!showInstructions)}
              >
                View troubleshooting steps
              </Button>
              <Collapse in={showInstructions}>
                <div className="mt-2">
                  <strong>Firefox:</strong>
                  <ol className="mb-0 ps-3">
                    <li>Go to Settings → Privacy & Security</li>
                    <li>Set "Enhanced Tracking Protection" to Standard</li>
                    <li>Refresh this page</li>
                  </ol>
                </div>
              </Collapse>
            </div>
            <Button
              variant="primary"
              size="sm"
              onClick={handleOpenForm}
              className="d-inline-flex align-items-center gap-1 flex-shrink-0"
            >
              <span>Open Form</span>
              <FontAwesomeIcon icon={faExternalLinkAlt} />
            </Button>
          </div>
        </Alert>

        <Card className="h-100">
          <Card.Body className="p-0">
            <div
              style={{
                position: 'relative',
                height: '80vh',
                overflow: 'hidden'
              }}
            >
              <iframe
                src={url}
                title={title}
                style={{
                  position: 'absolute',
                  top: 0,
                  left: 0,
                  width: '100%',
                  height: '100%',
                  border: 0
                }}
                allowFullScreen
                sandbox="allow-scripts allow-same-origin allow-forms allow-popups allow-presentation allow-popups-to-escape-sandbox"
                allow="geolocation; microphone; camera; fullscreen"
              />
            </div>
          </Card.Body>
        </Card>
      </Col>
    </Row>
  );
};

export default MicrosoftFormPage;
